// @ts-ignore – @types/mapbox__mapbox-sdk doesn't cover sub-path imports
import mapboxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import { useQuery } from '@tanstack/react-query';

import { MultiPolygon } from '@/api/rest/resources/types/plot';
import { MAPBOX_TOKEN } from '@/config/constants';

const geocodingClient = mapboxGeocoding({ accessToken: MAPBOX_TOKEN });

function computeCentroid(multiPolygon: MultiPolygon): [number, number] | null {
  const geometry = multiPolygon as unknown as { coordinates: number[][][][] };
  const coords = geometry.coordinates.flat(2);
  const lngs = coords.map((c) => Number(c[0])).filter(Number.isFinite);
  const lats = coords.map((c) => Number(c[1])).filter(Number.isFinite);
  if (!lngs.length || !lats.length) return null;
  const lng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const lat = (Math.min(...lats) + Math.max(...lats)) / 2;
  return [lng, lat];
}

/**
 * Derives a human-readable location string for a draft plot by reverse-geocoding
 * the centroid of its multi_polygon using the Mapbox Geocoding API.
 */
export function usePlotLocation(multiPolygon: MultiPolygon | null): {
  location: string | null;
  isLoading: boolean;
} {
  const centroid = multiPolygon ? computeCentroid(multiPolygon) : null;

  const query = useQuery({
    queryKey: ['reverse-geocode', centroid],
    queryFn: async () => {
      if (!centroid) return null;
      const response = await geocodingClient
        .reverseGeocode({ query: centroid, types: ['place', 'region', 'country'] })
        .send();
      return response.body.features[0]?.place_name ?? null;
    },
    enabled: !!centroid,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return { location: query.data ?? null, isLoading: query.isLoading };
}
